import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import DashboardView from '../views/DashboardView.vue';
import { useUserStore } from '@/stores';
import type { UserRole } from '@/types';
import oktaAuth from '@/config/okta';

// Auth routes (public - no authentication required)
const authRoutes: RouteRecordRaw[] = [
  {
    path: '/sign-in',
    name: 'SignIn',
    component: () => import('@/views/auth/SignInView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/completeLogin',
    name: 'LoginCallback',
    component: () => import('@/views/auth/CallbackView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/logout',
    name: 'Logout',
    component: () => import('@/views/auth/LogoutView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/account-deactivated',
    name: 'AccountDeactivated',
    component: () => import('@/views/auth/AccountDeactivatedView.vue'),
    meta: { requiresAuth: false },
  },
];

// Learner routes
const learnerRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'dashboard',
    component: DashboardView,
  },
  {
    path: '/assignments',
    name: 'assignments',
    component: () => import('../views/AssignmentsView.vue'),
  },
  // Cross-library keyword search from the header search bar. The only mixed
  // surface left after BPE-680: the "Browse All Courses" listing is gone, so
  // /browse without a keyword redirects to the Dashboard (legacyRoutes).
  {
    path: '/search',
    name: 'search',
    component: () => import('../views/BrowseView.vue'),
  },
  // BPE-599: the /browse/:category format×type routes (live-cle, cpe, skills, …)
  // are gone — Format is a filter inside a library page, not navigation. Old
  // category links break by design (a redirect would have to guess which
  // library of the kind).
  {
    path: '/browse/libraries/:libraryId(\\d+)',
    name: 'browse-library',
    component: () => import('../views/BrowseView.vue'),
  },
  {
    path: '/recommendations',
    name: 'recommendations',
    component: () => import('../views/RecommendationsView.vue'),
  },
  {
    path: '/pathways',
    name: 'pathways',
    component: () => import('../views/PathwaysView.vue'),
  },
  {
    path: '/pathways/:category',
    name: 'pathways-category',
    component: () => import('../views/PathwaysView.vue'),
  },
  {
    path: '/pathway/:id',
    name: 'pathway-detail',
    component: () => import('../views/PathwayDetailView.vue'),
  },
  {
    path: '/history',
    name: 'history',
    component: () => import('../views/HistoryView.vue'),
  },
  {
    path: '/activity',
    name: 'activity',
    component: () => import('../views/ActivityView.vue'),
  },
  {
    path: '/bookmarks',
    name: 'bookmarks',
    component: () => import('../views/BookmarksView.vue'),
  },
  {
    path: '/certificates',
    name: 'certificates',
    component: () => import('../views/CertificatesView.vue'),
  },
  {
    path: '/certificate/:id',
    name: 'certificate-detail',
    component: () => import('../views/CertificateView.vue'),
  },
  {
    path: '/course/:id/claim',
    name: 'claim-certificate',
    component: () => import('../views/ClaimCertificateView.vue'),
  },
  // Per-variant course detail pages (BPE-655): each variant has its own
  // independent page that renders exactly its own surfaces.
  {
    path: '/course/:id/variant/cle',
    name: 'course-detail-cle',
    component: () => import('../views/CLECourseDetailView.vue'),
  },
  {
    path: '/course/:id/variant/cpe',
    name: 'course-detail-cpe',
    component: () => import('../views/CPECourseDetailView.vue'),
  },
  {
    path: '/course/:id/variant/skills',
    name: 'course-detail-skills',
    component: () => import('../views/SkillsCourseDetailView.vue'),
  },
  {
    path: '/browse/libraries/:libraryId(\\d+)/practice-areas',
    name: 'microlearning-practice-areas',
    component: () => import('../views/MicrolearningIndexView.vue'),
  },
  {
    path: '/browse/libraries/:libraryId(\\d+)/practice-areas/:practiceAreaId(\\d+)',
    name: 'microlearning-practice-area',
    component: () => import('../views/MicrolearningPracticeAreaView.vue'),
  },
  {
    path: '/course/:id/variant/microlearning',
    name: 'course-detail-microlearning',
    component: () => import('../views/MicrolearningCourseDetailView.vue'),
  },
  {
    path: '/course/:id/variant/simulation',
    name: 'course-detail-simulation',
    component: () => import('../views/SimulationCourseDetailView.vue'),
  },
  {
    // Variantless fallback — old bookmarks, emails, dashboard cards.
    // Fetches course data and redirects to the first accessible variant.
    path: '/course/:id',
    name: 'course-detail',
    component: () => import('../views/CourseDetailView.vue'),
  },
  {
    path: '/settings/edit',
    name: 'settings-edit',
    component: () => import('../views/SettingsEditView.vue'),
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('../views/SettingsView.vue'),
  },
  {
    path: '/live-courses',
    name: 'live-courses',
    component: () => import('../views/LiveCoursesView.vue'),
  },
  {
    path: '/whoami',
    name: 'whoami',
    component: () => import('../views/WhoAmIView.vue'),
  },
  {
    path: '/onboarding',
    name: 'onboarding',
    component: () => import('../views/OnboardingView.vue'),
    meta: { standalone: true, isOnboarding: true },
  },
  {
    path: '/support',
    name: 'support',
    component: () => import('../views/Support.vue'),
  },
  // User Guide routes
  {
    path: '/guide/navigation',
    name: 'guide-navigation',
    component: () => import('../views/guide/GuideNavigation.vue'),
  },
  {
    path: '/guide/courses',
    name: 'guide-courses',
    component: () => import('../views/guide/GuideCourses.vue'),
  },
  {
    path: '/guide/course-types',
    name: 'guide-course-types',
    component: () => import('../views/guide/GuideCourseTypes.vue'),
  },
  {
    path: '/guide/profile',
    name: 'guide-profile',
    component: () => import('../views/guide/GuideProfile.vue'),
  },
  {
    path: '/guide/certificates',
    name: 'guide-certificates',
    component: () => import('../views/guide/GuideCertificates.vue'),
  },
];

// Org Admin routes
const orgAdminRoutes: RouteRecordRaw[] = [
  {
    path: '/admin',
    name: 'admin-dashboard',
    component: () => import('../views/org-admin/AdminDashboard.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/library',
    name: 'admin-library',
    component: () => import('../views/org-admin/ContentLibrary.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/libraries/:libraryId(\\d+)',
    name: 'admin-library-detail',
    component: () => import('../views/org-admin/ContentLibrary.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/courses/:id',
    name: 'admin-course-detail',
    component: () => import('../views/org-admin/CourseDetail.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/live-courses',
    name: 'admin-live-courses',
    component: () => import('../views/org-admin/LiveCourses.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/pathways',
    name: 'admin-pathways',
    component: () => import('../views/org-admin/Pathways.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/pathways/:category',
    name: 'admin-pathways-category',
    component: () => import('../views/org-admin/Pathways.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/pathways/create',
    name: 'admin-pathways-create',
    component: () => import('../views/org-admin/PathwayCreate.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/pathways/:id(\\d+)',
    name: 'admin-pathway-detail',
    component: () => import('../views/org-admin/PathwayDetail.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/users',
    name: 'admin-users',
    component: () => import('../views/org-admin/Users.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/users/import',
    name: 'admin-users-import',
    component: () => import('../views/org-admin/UserCsvImportView.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/users/:id/edit',
    name: 'admin-user-edit',
    component: () => import('../views/org-admin/UserEditView.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/users/:id',
    name: 'admin-user-detail',
    component: () => import('../views/org-admin/UserDetail.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/groups',
    name: 'admin-groups',
    component: () => import('../views/org-admin/Groups.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/groups/:id',
    name: 'admin-group-detail',
    component: () => import('../views/org-admin/GroupDetail.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/admins',
    name: 'admin-admins',
    component: () => import('../views/org-admin/Admins.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/activity-report',
    name: 'admin-activity-report',
    component: () => import('../views/org-admin/ActivityReport.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/settings',
    name: 'admin-settings',
    component: () => import('../views/org-admin/Settings.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  // Assignment wizards
  {
    path: '/admin/courses/:id/assign',
    name: 'admin-course-assign',
    component: () => import('../views/org-admin/CourseAssign.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/courses/:id/assign/review',
    name: 'admin-course-assign-review',
    component: () => import('../views/org-admin/CourseAssignReview.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/users/:id/assign',
    name: 'admin-user-assign',
    component: () => import('../views/org-admin/UserAssign.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/pathways/:id(\\d+)/assign',
    name: 'admin-pathway-assign',
    component: () => import('../views/org-admin/PathwayAssign.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/pathways/:id(\\d+)/assign/review',
    name: 'admin-pathway-assign-review',
    component: () => import('../views/org-admin/PathwayAssignReview.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  // Group management
  {
    path: '/admin/groups/create',
    name: 'admin-group-create',
    component: () => import('../views/org-admin/GroupCreate.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/groups/:id/members',
    name: 'admin-group-members',
    component: () => import('../views/org-admin/GroupMembers.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/groups/:id/pathways',
    name: 'admin-group-pathways',
    component: () => import('../views/org-admin/GroupPathways.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/groups/:id/courses',
    name: 'admin-group-courses',
    component: () => import('../views/org-admin/GroupCourses.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  // Activity and support
  {
    path: '/admin/activity',
    name: 'admin-activity',
    component: () => import('../views/org-admin/Activity.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/support',
    name: 'admin-support',
    component: () => import('../views/Support.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  // Org admin guide pages
  {
    path: '/admin/guide/dashboard',
    name: 'admin-guide-dashboard',
    component: () => import('../views/org-admin/guide/GuideDashboard.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/guide/content',
    name: 'admin-guide-content',
    component: () => import('../views/org-admin/guide/GuideContent.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/guide/pathways',
    name: 'admin-guide-pathways',
    component: () => import('../views/org-admin/guide/GuidePathways.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/guide/people',
    name: 'admin-guide-people',
    component: () => import('../views/org-admin/guide/GuidePeople.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/guide/assignments',
    name: 'admin-guide-assignments',
    component: () => import('../views/org-admin/guide/GuideAssignments.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/guide/settings',
    name: 'admin-guide-settings',
    component: () => import('../views/org-admin/guide/GuideSettings.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/guide/contracts',
    name: 'admin-guide-contracts',
    component: () => import('../views/org-admin/guide/GuideContracts.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  // Pathway creation
  {
    path: '/admin/pathways/create/manual',
    name: 'admin-pathway-create-manual',
    component: () => import('../views/org-admin/PathwayCreateManual.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/pathways/create/ai',
    name: 'admin-pathway-create-ai',
    component: () => import('../views/org-admin/PathwayCreateAi.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/pathways/ai-results',
    name: 'admin-pathway-ai-results',
    component: () => import('../views/org-admin/PathwayAiResults.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/pathways/:id(\\d+)/edit',
    name: 'admin-pathway-edit',
    component: () => import('../views/org-admin/PathwayEdit.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  // Settings sub-pages
  {
    path: '/admin/settings/brand',
    name: 'admin-settings-brand',
    component: () => import('../views/org-admin/SettingsBrand.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/settings/competency',
    name: 'admin-settings-competency',
    component: () => import('../views/org-admin/SettingsCompetency.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
  {
    path: '/admin/settings/integrations',
    name: 'admin-settings-integrations',
    component: () => import('../views/org-admin/SettingsIntegrations.vue'),
    meta: { requiresRole: 'org_admin' as UserRole },
  },
];

// Super Admin routes
const superAdminRoutes: RouteRecordRaw[] = [
  {
    path: '/super-admin',
    name: 'super-admin-dashboard',
    component: () => import('../views/super-admin/SuperDashboard.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/organizations',
    name: 'super-admin-organizations',
    component: () => import('../views/super-admin/Organizations.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/organization-new',
    name: 'super-admin-organization-new',
    component: () => import('../views/super-admin/OrganizationForm.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/organizations/:id/edit',
    name: 'super-admin-organization-edit',
    component: () => import('../views/super-admin/OrganizationForm.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/organizations/:id',
    name: 'super-admin-organization-detail',
    component: () => import('../views/super-admin/OrganizationDetail.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/contracts/:id',
    name: 'super-admin-contract-detail',
    component: () => import('../views/super-admin/ContractDetailView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/users',
    name: 'super-admin-users',
    component: () => import('../views/super-admin/AllUsers.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/users/import',
    name: 'super-admin-users-import',
    component: () => import('../views/super-admin/UserCsvImportView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/users/:id/edit',
    name: 'super-admin-user-edit',
    component: () => import('../views/super-admin/UserEditView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/users/:id',
    name: 'super-admin-user-detail',
    component: () => import('../views/super-admin/UserDetailView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/user-new',
    name: 'super-admin-user-new',
    component: () => import('../views/super-admin/UserFormView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/admins',
    name: 'super-admin-admins',
    component: () => import('../views/super-admin/SuperAdmins.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/courses',
    name: 'super-admin-courses',
    component: () => import('../views/super-admin/ContentLibrary.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/courses/:id',
    name: 'super-admin-course-detail',
    component: () => import('../views/super-admin/CourseDetailView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/courses/new',
    name: 'super-admin-course-new',
    component: () => import('../views/super-admin/CourseFormView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/courses/:id/edit',
    name: 'super-admin-course-edit',
    component: () => import('../views/super-admin/CourseFormView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/simulations/new',
    name: 'super-admin-simulation-new',
    component: () => import('../views/super-admin/SimulationFormView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/pathways',
    name: 'super-admin-pathways',
    component: () => import('../views/super-admin/Pathways.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/pathway/:id',
    name: 'super-admin-pathway-detail',
    component: () => import('../views/super-admin/PathwayDetailView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/live-event-new',
    name: 'super-admin-live-event-new',
    component: () => import('../views/super-admin/LiveEventFormView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    // MVP-C: confirmation surface after the date-change notification fans out.
    // Renders per-recipient SendGrid acceptance from router state. Direct
    // navigation falls back to an empty-state with a back-to-course link.
    path: '/super-admin/live-event/:eventId/date-change-confirmation',
    name: 'super-admin-live-event-date-change-confirmation',
    component: () => import('../views/super-admin/LiveEventDateChangeConfirmation.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/pathway-new',
    name: 'super-admin-pathway-new',
    component: () => import('../views/super-admin/PathwayFormView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/pathway/:id/edit',
    name: 'super-admin-pathway-edit',
    component: () => import('../views/super-admin/PathwayFormView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/pathway-create',
    name: 'super-admin-pathway-create',
    component: () => import('../views/org-admin/PathwayCreate.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/pathway-create-ai',
    name: 'super-admin-pathway-create-ai',
    component: () => import('../views/org-admin/PathwayCreateAi.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/pathway-ai-results',
    name: 'super-admin-pathway-ai-results',
    component: () => import('../views/org-admin/PathwayAiResults.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  // Catalog Data routes
  {
    path: '/super-admin/marketing-campaigns',
    name: 'super-admin-marketing-campaigns',
    component: () => import('../views/super-admin/marketing/MarketingCampaignTokens.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/marketing-campaigns/:id/edit',
    name: 'super-admin-marketing-campaign-edit',
    component: () => import('../views/super-admin/marketing/MarketingCampaignEdit.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/libraries',
    name: 'super-admin-libraries',
    component: () => import('../views/super-admin/catalog/Libraries.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/library/:id',
    name: 'super-admin-library-detail',
    component: () => import('../views/super-admin/LibraryDetailView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/library-new',
    name: 'super-admin-library-new',
    component: () => import('../views/super-admin/LibraryFormView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/library/:id/edit',
    name: 'super-admin-library-edit',
    component: () => import('../views/super-admin/LibraryFormView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/pass-types',
    name: 'super-admin-pass-types',
    component: () => import('../views/super-admin/catalog/PassTypes.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/pass-type-edit',
    name: 'super-admin-pass-type-new',
    component: () => import('../views/super-admin/catalog/PassTypeEdit.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/pass-types/:id/edit',
    name: 'super-admin-pass-type-edit',
    component: () => import('../views/super-admin/catalog/PassTypeEdit.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/accreditations',
    name: 'super-admin-accreditations',
    component: () => import('../views/super-admin/catalog/Accreditations.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/accreditation-new',
    name: 'super-admin-accreditation-new',
    component: () => import('../views/super-admin/catalog/AccreditationEdit.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/accreditation/:id/edit',
    name: 'super-admin-accreditation-edit',
    component: () => import('../views/super-admin/catalog/AccreditationEdit.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/courses/import',
    name: 'super-admin-courses-import',
    component: () => import('../views/super-admin/catalog/CourseImportView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/bulk-update',
    name: 'super-admin-bulk-update',
    component: () => import('../views/super-admin/catalog/BulkUpdateView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/courses/import-content',
    name: 'super-admin-content-import',
    component: () => import('../views/super-admin/catalog/ContentImportView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/courses/import-skillburst',
    name: 'super-admin-skillburst-import',
    component: () => import('../views/super-admin/catalog/SkillburstImportView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/courses/import-microlearning',
    name: 'super-admin-microlearning-import',
    component: () => import('../views/super-admin/catalog/MicrolearningImportView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/jurisdictions',
    name: 'super-admin-jurisdictions',
    component: () => import('../views/super-admin/catalog/Jurisdictions.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/jurisdiction-new',
    name: 'super-admin-jurisdiction-new',
    component: () => import('../views/super-admin/catalog/JurisdictionFormView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/jurisdiction/:id/edit',
    name: 'super-admin-jurisdiction-edit',
    component: () => import('../views/super-admin/catalog/JurisdictionFormView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/credit-types',
    name: 'super-admin-credit-types',
    component: () => import('../views/super-admin/catalog/CreditTypes.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/credit-type-edit',
    name: 'super-admin-credit-type-new',
    component: () => import('../views/super-admin/catalog/CreditTypeEdit.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/credit-types/:id/edit',
    name: 'super-admin-credit-type-edit',
    component: () => import('../views/super-admin/catalog/CreditTypeEdit.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/practice-areas',
    name: 'super-admin-practice-areas',
    component: () => import('../views/super-admin/catalog/PracticeAreas.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/practice-area-edit',
    name: 'super-admin-practice-area-edit',
    component: () => import('../views/super-admin/catalog/PracticeAreaFormView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/practice-areas/:id/courses',
    name: 'PracticeAreaCourses',
    component: () => import('../views/browse/PracticeAreaCourses.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/skills',
    name: 'super-admin-skills',
    component: () => import('../views/super-admin/catalog/Skills.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/skill-new',
    name: 'super-admin-skill-new',
    component: () => import('../views/super-admin/catalog/SkillFormView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/skill/:id/edit',
    name: 'super-admin-skill-edit',
    component: () => import('../views/super-admin/catalog/SkillFormView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  // Microlearning Practice Areas (BPE-677 / BPE-695)
  {
    path: '/super-admin/ml-practice-areas',
    name: 'super-admin-ml-practice-areas',
    component: () => import('../views/super-admin/catalog/MicrolearningPracticeAreas.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/ml-practice-area-new',
    name: 'super-admin-ml-practice-area-new',
    component: () => import('../views/super-admin/catalog/MicrolearningPracticeAreaFormView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/ml-practice-areas/:id',
    name: 'super-admin-ml-practice-area-detail',
    component: () => import('../views/super-admin/catalog/MicrolearningPracticeAreaDetailView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/ml-practice-areas/:id/edit',
    name: 'super-admin-ml-practice-area-edit',
    component: () => import('../views/super-admin/catalog/MicrolearningPracticeAreaFormView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  // Competencies (BPE-698) — the curated vocabulary simulations are tagged with.
  // Adding one here is how a competency added on Apex's side reaches Halo.
  {
    path: '/super-admin/competencies',
    name: 'super-admin-competencies',
    component: () => import('../views/super-admin/catalog/Competencies.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/competency-new',
    name: 'super-admin-competency-new',
    component: () => import('../views/super-admin/catalog/CompetencyFormView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/competency/:id/edit',
    name: 'super-admin-competency-edit',
    component: () => import('../views/super-admin/catalog/CompetencyFormView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/presenters',
    name: 'super-admin-presenters',
    component: () => import('../views/super-admin/catalog/Presenters.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/skills/:id/courses',
    name: 'SkillCourses',
    component: () => import('../views/browse/SkillCourses.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  // Reports routes
  {
    path: '/super-admin/reports/organizations',
    name: 'super-admin-reports-organizations',
    component: () => import('../views/super-admin/reports/OrganizationsReport.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/reports/users',
    name: 'super-admin-reports-users',
    component: () => import('../views/super-admin/reports/UsersReport.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/reports/content',
    name: 'super-admin-reports-content',
    component: () => import('../views/super-admin/reports/ContentReport.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/reports/jurisdictions',
    name: 'super-admin-reports-jurisdictions',
    component: () => import('../views/super-admin/reports/JurisdictionsReport.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    // U9: super-admin "Who to apply for" work-queue report.
    path: '/super-admin/reports/who-to-apply-for',
    name: 'super-admin-reports-who-to-apply-for',
    component: () => import('../views/super-admin/reports/WhoToApplyFor.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/analytics',
    name: 'super-admin-analytics',
    component: () => import('../views/super-admin/reports/Analytics.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  // Settings
  {
    path: '/super-admin/settings',
    name: 'super-admin-settings',
    component: () => import('../views/super-admin/Settings.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/settings/system',
    name: 'super-admin-settings-system',
    component: () => import('../views/super-admin/settings/SystemSettings.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/settings/certificate-templates',
    name: 'super-admin-certificate-templates',
    component: () => import('../views/super-admin/settings/CertificateTemplates.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  // Product Feedback routes
  {
    path: '/super-admin/bugs',
    name: 'super-admin-bugs',
    component: () => import('../views/super-admin/feedback/Bugs.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/feedback',
    name: 'super-admin-feedback',
    component: () => import('../views/super-admin/feedback/Backlog.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/ideas',
    name: 'super-admin-ideas',
    component: () => import('../views/super-admin/feedback/Ideas.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  // Search Console
  {
    path: '/super-admin/search',
    name: 'super-admin-search',
    component: () => import('../views/super-admin/search/SearchConsoleView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  // Video Migration
  {
    path: '/super-admin/video-migration',
    name: 'super-admin-video-migration',
    component: () => import('../views/super-admin/VideoMigrationView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  // Salesforce nightly sync (Phase 3 of the SF sync plan)
  {
    path: '/super-admin/salesforce-sync',
    name: 'super-admin-salesforce-sync',
    component: () => import('../views/super-admin/SalesforceSyncView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  // External CPE Assessments mirrored from quiz-generator
  {
    path: '/super-admin/external-assessments',
    name: 'super-admin-external-assessments',
    component: () => import('../views/super-admin/ExternalAssessmentsView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  // Beacon Audit Batches (credit-audit drain worker)
  {
    path: '/super-admin/beacon-audit-batches',
    name: 'super-admin-beacon-audit-batches',
    component: () => import('../views/super-admin/BeaconAuditBatchesView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/beacon-audit-batches/:id',
    name: 'super-admin-beacon-audit-batch-detail',
    component: () => import('../views/super-admin/BeaconAuditBatchDetailView.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  // User Guide routes
  {
    path: '/super-admin/guide/organizations',
    name: 'super-admin-guide-organizations',
    component: () => import('../views/super-admin/guide/GuideOrganizations.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/guide/users',
    name: 'super-admin-guide-users',
    component: () => import('../views/super-admin/guide/GuideUsers.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/guide/competencies',
    name: 'super-admin-guide-competencies',
    component: () => import('../views/super-admin/guide/GuideCompetencies.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/guide/ml-practice-areas',
    name: 'super-admin-guide-ml-practice-areas',
    component: () => import('../views/super-admin/guide/GuideMlPracticeAreas.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/guide/content',
    name: 'super-admin-guide-content',
    component: () => import('../views/super-admin/guide/GuideContent.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/guide/beacon-audit',
    name: 'super-admin-guide-beacon-audit',
    component: () => import('../views/super-admin/guide/GuideBeaconAudit.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/guide/salesforce-sync',
    name: 'super-admin-guide-salesforce-sync',
    component: () => import('../views/super-admin/guide/GuideSalesforceSync.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/guide/marketing-campaigns',
    name: 'super-admin-guide-marketing-campaigns',
    component: () => import('../views/super-admin/guide/GuideMarketingCampaigns.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/guide/credit-management',
    name: 'super-admin-guide-credit-management',
    component: () => import('../views/super-admin/guide/GuideCreditManagement.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/guide/course-fields',
    name: 'super-admin-guide-course-fields',
    component: () => import('../views/super-admin/guide/GuideCourseFields.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/guide/imports',
    name: 'super-admin-guide-imports',
    component: () => import('../views/super-admin/guide/GuideImports.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
  {
    path: '/super-admin/guide/contracts',
    name: 'super-admin-guide-contracts',
    component: () => import('../views/super-admin/guide/GuideContracts.vue'),
    meta: { requiresRole: 'super_admin' as UserRole },
  },
];

// Mock Beacon page. Always registered — the real gate is the backend's
// BeaconLive:UseMock flag, which controls whether /api/mock/beacon/attend
// exists. Without that endpoint the page is inert, so there's no value in
// a separate frontend gate (and a build-time DEV check disagrees with the
// backend in QA builds, where the mock is on but DEV is false).
const mockRoutes: RouteRecordRaw[] = [
  {
    path: '/mock/beacon-live',
    name: 'mock-beacon-live',
    component: () => import('../views/mock/MockBeaconLive.vue'),
  },
];

// Legacy routes — stale bookmarks land on the Dashboard, whose Browse
// Libraries section is where a learner picks a library to browse.
//
// /lectures, /webinars, /courses are BARBRI's previous platform. /browse was
// the "Browse All Courses" listing removed in BPE-680; a bookmarked keyword
// search (/browse?q=…) keeps working by moving to the search route.
const legacyRoutes: RouteRecordRaw[] = [
  { path: '/lectures', redirect: '/' },
  { path: '/webinars', redirect: '/' },
  { path: '/courses', redirect: '/' },
  {
    path: '/browse',
    redirect: (to) => (to.query.q ? { name: 'search', query: to.query } : { name: 'dashboard' }),
  },
];

// Catch-all: unmatched paths render an explicit "page not found" view instead
// of a blank <RouterView /> body inside AppLayout (which reads as a dead click).
// Must come last so legitimate routes match first.
const notFoundRoute: RouteRecordRaw = {
  path: '/:pathMatch(.*)*',
  name: 'not-found',
  component: () => import('../views/NotFoundView.vue'),
};

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    ...authRoutes,
    ...learnerRoutes,
    ...orgAdminRoutes,
    ...superAdminRoutes,
    ...mockRoutes,
    ...legacyRoutes,
    notFoundRoute,
  ],
});

// Navigation guard: authentication required by default, unless meta.requiresAuth === false
router.beforeEach(async (to, _from, next) => {
  // Skip auth check only for routes explicitly marked as public
  if (to.meta.requiresAuth === false) {
    next();
    return;
  }

  // All other routes require authentication
  const isAuthenticated = await oktaAuth.isAuthenticated();
  if (!isAuthenticated) {
    localStorage.setItem('pe:returnUrl', to.fullPath);
    next({ name: 'SignIn' });
    return;
  }

  // Ensure user profile is loaded before navigating to protected routes
  const userStore = useUserStore();
  if (!userStore.profile) {
    await userStore.fetchProfile();
  }

  // If profile fetch was blocked by deactivation or no-org, redirect to the blocked page.
  if (!userStore.profile) {
    const reason = userStore.blockReason;
    next({ name: 'AccountDeactivated', query: reason ? { reason } : undefined });
    return;
  }

  // Infer the active role from the route so the role switcher reflects the current view.
  if (to.path.startsWith('/super-admin') && userStore.hasRole('super_admin')) {
    userStore.setRole('super_admin');
  } else if (to.path.startsWith('/admin') && userStore.hasRole('org_admin')) {
    userStore.setRole('org_admin');
  } else {
    userStore.setRole('learner');
  }

  // Redirect learners who haven't completed onboarding
  if (!userStore.isOnboardingComplete && !to.meta.isOnboarding) {
    next({ name: 'onboarding' });
    return;
  }

  // Check role requirements
  const requiredRole = to.meta.requiresRole as UserRole | undefined;
  if (requiredRole) {
    if (!userStore.hasRole(requiredRole)) {
      next({ name: 'dashboard' });
      return;
    }
  }

  next();
});

export default router;
